import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useRegionStore from '@/context/RegionStore';
import { ensureRegionPrefix } from '@/utils/regionDetection';

export type ButtonVariant =
  | 'border-white'
  | 'border-black'
  | 'border-orange'
  | 'border-creator'
  | 'solid-white'
  | 'solid-black'
  | 'solid-orange'
  | 'solid-yellow'
  | 'solid-butter'
  | 'solid-creator'
  | 'solid-midnight'
  | 'solid-red'
  | 'solid-green'
  | 'solid-mudpie'
  | 'solid-forest'
  | 'solid-dreampop';

interface ButtonProps {
  to?: string;
  /** External or absolute URL; renders `<a>` instead of react-router `<Link>`. */
  href?: string;
  target?: string;
  rel?: string;
  text: string;
  variant?: ButtonVariant;
  showArrow?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

type NavButtonBaseProps = {
  className?: string;
  children: React.ReactNode;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
};

type NavButtonLinkProps = NavButtonBaseProps & {
  to: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type NavButtonActionProps = NavButtonBaseProps & {
  to?: undefined;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
};

export type NavButtonProps = NavButtonLinkProps | NavButtonActionProps;

export const getVariantClasses = (variant: ButtonVariant): string => {
  const variants = {
    'border-white': 'border border-white text-white hover:scale-x-105',
    'border-black': 'border border-black text-black hover:scale-x-105',
    'border-orange': 'border border-orange-500 text-orange-500 hover:scale-x-105',
    'border-creator': 'border border-[#6146FE] text-[#6146FE] hover:scale-x-105',
    'solid-white': 'bg-white text-black hover:scale-x-105',
    'solid-black': 'bg-black text-white hover:scale-x-105',
    'solid-orange': 'bg-orange text-white hover:scale-x-105',
    'solid-yellow': 'bg-specialYellow text-black hover:scale-x-105',
    'solid-green': 'bg-darkGreen text-white hover:scale-x-105',
    'solid-butter': 'bg-gradient-to-r from-yellow-300 to-amber-400 text-amber-900 bg-origin-border hover:scale-x-105 hover:from-yellow-400 hover:to-amber-400 font-medium',
    'solid-midnight': 'bg-gradient-to-r from-blue-800 to-indigo-600 text-white bg-origin-border hover:scale-x-105 hover:from-indigo-700 hover:to-indigo-700 font-medium',
    'solid-forest': 'bg-gradient-to-r from-[#31B05E] to-[#1E9348] text-white bg-origin-border hover:scale-x-105 hover:from-green-700 hover:to-emerald-900 font-medium',
    'solid-creator': 'bg-gradient-to-r from-[#684FF8] to-[#6146FE] text-white bg-origin-border hover:scale-x-105 font-medium',
    'solid-mudpie': 'bg-gradient-to-r from-[#885836] to-[#A46F4A] text-white bg-origin-border hover:scale-x-105 hover:from-[#A46F4A] hover:to-[#A46F4A] font-medium',
    'solid-dreampop': 'bg-gradient-to-r from-pink-500 to-rose-500 text-white bg-origin-border hover:scale-x-105 hover:from-pink-400 hover:to-rose-400 font-medium',
    'solid-red': 'bg-gradient-to-r from-[#C9291F] to-[#7D110B] text-white bg-origin-border hover:scale-x-105 hover:from-[#B9241B] hover:to-[#720F09] font-medium'
  };

  return variants[variant];
};

const navButtonBaseClassName = 'text-white px-4 h-12 rounded-lg border bg-clip-padding md:hover:bg-white/25 transition-colors font-headline leading-none flex items-center justify-center gap-1';

export const NavButton: React.FC<NavButtonProps> = (props) => {
  const mergedClassName = `${navButtonBaseClassName} ${props.className || ''}`.trim();

  if ('to' in props && props.to) {
    const { to, onClick, onMouseEnter, onMouseLeave, children } = props as NavButtonLinkProps;
    return (
      <Link
        to={to}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={mergedClassName}
      >
        {children}
      </Link>
    );
  }

  const { type = 'button', onClick, onMouseEnter, onMouseLeave, children } = props as NavButtonActionProps;
  return (
    <button
      type={type}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={mergedClassName}
    >
      {children}
    </button>
  );
};

const Button: React.FC<ButtonProps> = ({
  to,
  href,
  target,
  rel,
  text,
  variant = 'border-white',
  showArrow = true,
  onClick,
  className = ''
}) => {
  const { chosenRegion, detectedRegion, storeRegion } = useRegionStore();
  const baseClassName = "inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300";
  const variantClassName = getVariantClasses(variant);
  const buttonClassName = `${baseClassName} ${variantClassName} ${className}`;
  const isInternalHref = href && href.startsWith('/') && !href.startsWith('//');
  const resolvedInternalHref = isInternalHref
    ? ensureRegionPrefix(href, chosenRegion || detectedRegion || storeRegion || 'gb')
    : null;

  if (resolvedInternalHref) {
    return (
      <Link
        to={resolvedInternalHref}
        className={buttonClassName}
      >
        {text}
        {showArrow && <ArrowRight className="w-5 h-5" />}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={buttonClassName}
        target={target}
        rel={rel}
      >
        {text}
        {showArrow && <ArrowRight className="w-5 h-5" />}
      </a>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={buttonClassName}
      >
        {text}
        {showArrow && <ArrowRight className="w-5 h-5" />}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      className={buttonClassName}
    >
      {text}
      {showArrow && <ArrowRight className="w-5 h-5" />}
    </button>
  );
};

export default Button; 
